import { Directive, HostListener, Input, OnInit, OnChanges, SimpleChanges, ElementRef, Renderer2 } from '@angular/core';
import { buildCodeFinancialTooltip } from '../util/code-financial-tooltip.util';

@Directive({
  selector: '[appProviderInitialsTooltip]',
  exportAs: 'appProviderInitialsTooltip'
})
export class ProviderInitialsTooltipDirective implements OnInit, OnChanges {
  @Input('appProviderInitialsTooltip') procedureCode: any;
  @Input() existingTooltip: string = '';
  @Input() showProviderInitials: boolean = true;
  /** Opt-in: append the code's Connect financial breakdown, built lazily on hover. */
  @Input() showFinancialTooltip: boolean = false;

  private _cachedTooltip: string = '';
  private _lastProcedureCodeId: string = '';
  private _lastExistingTooltip: string = '';
  private _lastShowProviderInitials: boolean = true;
  /** null = not yet built for the current code; '' = built, nothing to show. */
  private _financialTooltip: string | null = null;

  /** Construct the financial breakdown only when the user actually hovers. */
  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.showFinancialTooltip && this._financialTooltip === null) {
      this._financialTooltip = buildCodeFinancialTooltip(this.procedureCode) || '';
      // Bust the memo so computedTooltip recomputes with the financial part.
      this._lastProcedureCodeId = '';
    }
  }

  // Computed tooltip text that includes provider initials
  get computedTooltip(): string {
    // Check if we need to recalculate
    const currentProcedureCodeId = this.procedureCode?.id || '';
    const currentExistingTooltip = this.existingTooltip || '';
    const currentShowProviderInitials = this.showProviderInitials;
    
    if (this._lastProcedureCodeId === currentProcedureCodeId && 
        this._lastExistingTooltip === currentExistingTooltip &&
        this._lastShowProviderInitials === currentShowProviderInitials) {
      return this._cachedTooltip;
    }

    // Cache the current values
    this._lastProcedureCodeId = currentProcedureCodeId;
    this._lastExistingTooltip = currentExistingTooltip;
    this._lastShowProviderInitials = currentShowProviderInitials;

    if (!this.procedureCode) {
      this._cachedTooltip = this.existingTooltip || '';
      return this._cachedTooltip;
    }

    let tooltipText = this.existingTooltip || '';
    // Check if the procedure code has a provider attached and we should show initials
    if (this.showProviderInitials && this.procedureCode.provider) {
      const provider = this.procedureCode.provider;
      const initials = this.getProviderInitials(provider);
      
      if (initials) {
        // Add initials at the beginning of the tooltip
        if (tooltipText) {
          tooltipText = `${initials} - ${tooltipText}`;
        } else {
          tooltipText = initials;
        }
      }
    }

    // Append the lazily-built financial breakdown (extends, never replaces).
    if (this.showFinancialTooltip && this._financialTooltip) {
      tooltipText = tooltipText ? `${tooltipText}\n${this._financialTooltip}` : this._financialTooltip;
    }

    this._cachedTooltip = tooltipText;
    return this._cachedTooltip;
  }

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  ngOnInit() {
    // Initialize cache
    this._cachedTooltip = '';
    this._lastProcedureCodeId = '';
    this._lastExistingTooltip = '';
  }

  ngOnChanges(changes: SimpleChanges) {
    // Clear cache when inputs change to force recalculation
    if (changes['procedureCode'] || changes['existingTooltip'] || changes['showProviderInitials']) {
      this._cachedTooltip = '';
      this._lastProcedureCodeId = '';
      this._lastExistingTooltip = '';
      this._lastShowProviderInitials = false;
    }
    // A different code needs its financial breakdown rebuilt on next hover.
    if (changes['procedureCode']) {
      this._financialTooltip = null;
    }
  }

  private getProviderInitials(provider: any): string {
    if (!provider) {
      return '';
    }

    const { title, name, surname } = provider;
    let initials = '';

    // Add title if available
    /* if (title && title.trim()) {
      initials += title.trim();
    } */

    // Add first letter of name if available
    if (name && name.trim()) {
      initials += name.trim().charAt(0).toUpperCase();
    }

    // Add first letter of surname if available
    if (surname && surname.trim()) {
      initials += surname.trim().charAt(0).toUpperCase();
    }

    return initials;
  }
}

